import { getDefaultCountry } from '@yembo/yemblocks-core/dist/_util';
import dayjs, { Dayjs } from 'dayjs';
import { Company } from 'src/types';
import { getHoursDifference } from '../../../_util/dates/getDate';

const currentOffset = dayjs().utcOffset();

export const removeCurrentOffset = (date: Date | Dayjs | string) => dayjs(date).subtract(currentOffset, 'minutes');
export const addCurrentOffset = (date: Date | Dayjs | string) => dayjs(date).add(currentOffset, 'minutes');

export const getDateFormatString = (country: string, showShortened = false) => {
  const monthFirstCountries = ['US', 'CA']; // manually adding countries that use month first formatting. Can't use moment 'L' formatting with google's date picker
  const showMonthFirst = monthFirstCountries.includes(country);

  if (showMonthFirst) return showShortened ? 'M/D' : 'MM/DD/YYYY';

  return showShortened ? 'D/M' : 'DD/MM/YYYY';
};

export const getDatePickerFormatting = ({
  company,
  companies,
}: {
  company?: { defaultCountry: string };
  companies?: Company[];
}) => {
  const country = getDefaultCountry({ company, companies: companies ?? [] });
  const monthFirstCountries = ['US', 'CA'];
  const showMonthFirst = monthFirstCountries.includes(country);

  if (showMonthFirst) return { dateFormat: 'MM/dd/yyyy', placeholderText: 'MM/DD/YYYY' };

  return { dateFormat: 'dd/MM/yyyy', placeholderText: 'DD/MM/YYYY' };
};

export const renderDateText = ({
  date,
  showDateWithTime = false,
  formatString,
}: {
  date?: string | null | number | Date;
  showDateWithTime?: boolean;
  formatString: string;
}) => {
  if (!date) return '--';

  const calendarOptions = {
    sameDay: '[Today], h:mm a',
    nextDay: '[Tomorrow], h:mm a',
    lastDay: '[Yesterday], h:mm a',
    sameElse: formatString,
  };

  const isValid = dayjs(date).isValid();
  const yesterday = dayjs().subtract(1, 'day').startOf('day');
  const sevenDaysAgo = dayjs().subtract(7, 'day').startOf('day');
  const isBetween = dayjs(date).startOf('day').isBetween(sevenDaysAgo, yesterday);
  const showRelativeDate = isBetween && showDateWithTime;
  const relativeDate = dayjs(date).fromNow();
  const dateWithTime = dayjs(date).calendar(null, calendarOptions);
  const monthDayYearDate = dayjs(date).format(formatString);

  if (!isValid) return '--';
  else if (showRelativeDate) return relativeDate;
  else if (showDateWithTime) return dateWithTime;
  else return monthDayYearDate;
};

export const renderDateWithColor = ({
  date,
  allowableHour,
  formatString,
}: {
  date: string | null | number | undefined;
  allowableHour: number;
  formatString: string;
}) => {
  const isValid = dayjs(date).isValid();

  if (!isValid) return '--';

  const hoursDifference = getHoursDifference(date);
  const showRed = hoursDifference > allowableHour;
  const className = showRed ? 'date-alert' : '';

  const dateText = renderDateText({
    date,
    showDateWithTime: true,
    formatString,
  });

  return <div className={className}>{dateText}</div>;
};
